import React, { useState } from "react";
import {
  Typography,
  Button,
  Box,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Slider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Link } from "react-router-dom";

const SLOT_TIERS = [
  { range: "1–10", price: 3, slots: 10 },
  { range: "11–30", price: 2, slots: 20 },
  { range: "31–60", price: 1.5, slots: 30 },
  { range: "61+", price: 1, slots: Infinity },
];

const calculateBreakdown = (slots) => {
  const breakdown = [];
  const billableSlots = Math.max(slots, 0);
  let remaining = billableSlots;
  let total = 0;
  let slotStart = billableSlots > 0 ? 1 : 0;

  for (const tier of SLOT_TIERS) {
    if (remaining <= 0) break;
    const slotsInTier = Math.min(
      remaining,
      tier.slots === Infinity ? remaining : tier.slots
    );
    const tierCost = slotsInTier * tier.price;
    const slotEnd = slotStart + slotsInTier - 1;
    const rangeLabel =
      tier.slots === Infinity && slotEnd === slotStart
        ? `${slotStart}+`
        : `${slotStart}–${slotEnd}`;
    breakdown.push({
      range: rangeLabel,
      slots: slotsInTier,
      pricePerSlot: tier.price,
      cost: tierCost,
    });
    total += tierCost;
    remaining -= slotsInTier;
    slotStart += slotsInTier;
  }

  return { breakdown, total };
};

const Pricing = () => {
  const [slots, setSlots] = useState(20);
  const { breakdown, total } = calculateBreakdown(slots);
  return (
    <Box
      sx={{
        maxWidth: 1000,
        mx: "auto",
        px: 4,
        py: 8,
      }}
    >
      {/* Pricing Summary */}
      <Box sx={{ textAlign: "center", mb: 8 }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 2,
            fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.75rem" },
          }}
        >
          Simple, predictable slot pricing
        </Typography>
        <Typography
          variant="h6"
          sx={{
            mb: 4,
            color: "text.secondary",
            fontWeight: 400,
            fontSize: { xs: "1rem", sm: "1.1rem" },
          }}
        >
          Pay per client slot — the more you grow, the less you pay per slot.
          Choose any number of clients you want; there are no predefined plans.
          First month is free so you can try out the platform before you pay.
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
          flexWrap="wrap"
          sx={{ mb: 4 }}
        >
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            ✓ First month is free
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            ✓ No setup fee
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            ✓ No contracts
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            ✓ Price drops as you scale
          </Typography>
        </Stack>
      </Box>

      {/* Marginal Slot Pricing */}
      <Paper elevation={2} sx={{ p: 4, mb: 6 }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontWeight: 700,
            mb: 1,
            fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
          }}
        >
          Marginal Slot Pricing
        </Typography>
        <Typography variant="body1" sx={{ mb: 2, color: "text.secondary" }}>
          Your monthly cost is based on your number of client slots. Each tier
          applies only to the slots in that range.
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: "text.secondary" }}>
          Choose any number of clients — there are no predefined plans. Scale up
          or down as your business grows.
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Slot Range</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Price Per Slot
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>1–10</TableCell>
                <TableCell align="right">$3.00</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>11–30</TableCell>
                <TableCell align="right">$2.00</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>31–60</TableCell>
                <TableCell align="right">$1.50</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>61+</TableCell>
                <TableCell align="right">$1.00</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Stack spacing={1} sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2">
              Stripe / Payments Pro Add-On: $10/month flat
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2">Not locked into buckets</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2">
              Per-slot rate drops automatically as you scale
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Price Calculator */}
      <Paper elevation={2} sx={{ p: 4, mb: 6 }}>
        <Typography
          variant="h5"
          component="h3"
          sx={{
            fontWeight: 600,
            mb: 1,
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
          }}
        >
          Calculate your cost
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
          Select your total number of client slots to see how they break down by
          pricing tier. New coaches get a one-month free trial before billing
          starts.
        </Typography>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Client slots: <strong>{slots}</strong>
          </Typography>
          <Slider
            value={slots}
            onChange={(_, value) => setSlots(value)}
            min={1}
            max={150}
            valueLabelDisplay="auto"
            marks={[
              { value: 10, label: "10" },
              { value: 30, label: "30" },
              { value: 60, label: "60" },
              { value: 100, label: "100" },
              { value: 150, label: "150" },
            ]}
            sx={{ maxWidth: 400 }}
          />
        </Box>
        <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Calculation by slot range:
          </Typography>
          <Stack spacing={1}>
            {breakdown.map((row) => (
              <Typography
                key={row.range}
                variant="body2"
                sx={{ fontFamily: "monospace" }}
              >
                {row.slots} slots in {row.range} × ${row.pricePerSlot} = $
                {row.cost.toFixed(0)}
              </Typography>
            ))}
          </Stack>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              mt: 2,
              pt: 2,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            Total: ${total}/month
          </Typography>
        </Box>
      </Paper>

      {/* What's Included */}
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h5"
          component="h3"
          sx={{
            fontWeight: 600,
            mb: 2,
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
          }}
        >
          What&apos;s included
        </Typography>
        <Stack spacing={1} sx={{ mb: 3, pl: 2 }}>
          <Typography variant="body2">
            • Programming and workout history
          </Typography>
          <Typography variant="body2">
            • Program and template creation
          </Typography>
          <Typography variant="body2">• Custom content uploads</Typography>
          <Typography variant="body2">• Messaging</Typography>
          <Typography variant="body2">• Workout importer</Typography>

          <Typography variant="body2">
            • Platform updates and support
          </Typography>
          <Typography variant="body2">
            • Payments Add-On: $10/month flat (optional)
          </Typography>
          <Typography variant="body2">
            • Client access control when payments stop (with payments add-on)
          </Typography>
        </Stack>
      </Box>

      {/* CTA */}
      <Box sx={{ textAlign: "center" }}>
        <Button
          variant="contained"
          component={Link}
          to="/coaching"
          size="large"
          sx={{
            px: 4,
            py: 1.5,
            fontSize: "1rem",
            fontWeight: 600,
          }}
        >
          Start coaching with Trainer App
        </Button>
      </Box>
    </Box>
  );
};

export default Pricing;
